/**
 * Subscription plans: what each tier allows and how usage is metered. The
 * module enforces these limits, the API maps Stripe prices onto them and the
 * web app shows them. Paid tiers are priced per editor seat (owners, admins
 * and editors); viewers and review-link guests are free.
 *
 * Storage counts originals plus everything derived from them (proxies,
 * exports). Render minutes are output minutes weighted by resolution, so a
 * 4K minute costs four 1080p minutes. "AI minutes" cover transcription,
 * voiceovers and motion tracking. Monthly allowances reset on the 1st (UTC).
 */

import { DEFAULT_AUDIO_FX, DEFAULT_COLOR, DEFAULT_CROP, DEFAULT_TEXT, DEFAULT_TRANSFORM, DEFAULT_VIDEO_FX } from './defaults';
import { estimateEncodeBytes, isMasterEncode, normalizeEncode, type EncodeSettings } from './formats';
import type { ClipData, TimelineSnapshot, TrackData } from './types';

export const PLAN_IDS = ['free', 'trial', 'pro', 'team', 'enterprise'] as const;
export type PlanId = (typeof PLAN_IDS)[number];
export const PAID_PLANS = ['pro', 'team', 'enterprise'] as const;
export type PaidPlanId = (typeof PAID_PLANS)[number];

/** Plans that are paid for per seat (and so have seats, add-ons and render packs). */
export const isPaidPlan = (id: string): id is PaidPlanId => (PAID_PLANS as readonly string[]).includes(id);
export type BillingInterval = 'month' | 'year';

export interface PlanSpec {
  id: PlanId;
  label: string;
  /** Editor seats (owners, admins, editors). Paid plans buy seats within this range. */
  minSeats: number;
  maxSeats: number;
  /** Viewer members (Infinity = unlimited). Review-link guests never count. */
  maxViewers: number;
  /** Storage for the first seat, and per additional seat (GB, including proxies and exports). */
  storageGb: number;
  storagePerExtraSeatGb: number;
  /** Monthly render minutes for the first seat, and per additional seat. */
  renderMinutes: number;
  renderMinutesPerExtraSeat: number;
  /** Monthly transcription / voiceover / tracking minutes. */
  aiMinutes: number;
  aiMinutesPerExtraSeat: number;
  /** Longest allowed short side of an export (720 = 720p, 2160 = 4K). */
  maxExportShortSide: number;
  /** Small "Made with vidmakr" mark on exports. */
  watermark: boolean;
  /** Finished exports are deleted after this many days (unless a live review link shows them). */
  exportRetentionDays: number;
  maxProjects: number;
  /** Longest a review link may stay open (days; links never last over 365). */
  reviewLinkMaxDays: number;
  /** Review links with several versions, drawings and approvals (free links: comments only). */
  reviewPro: boolean;
  /** Organization logo on review pages. */
  reviewBranding: boolean;
  /** AI agents through the MCP server. */
  mcp: boolean;
  /** ProRes and lossless exports. */
  masterFormats: boolean;
  /** Exports on the shared GPU pool. */
  gpu: boolean;
  /** The organization's own GPU workers. */
  ownWorkers: boolean;
  /** The large, more accurate transcription model. */
  accurateTranscripts: boolean;
  /** Jobs run ahead of other plans' jobs. */
  priority: boolean;
  /**
   * May pay by invoice (bank transfer on payment terms) instead of by card,
   * once a superadmin allows it for the organization.
   */
  invoicing: boolean;
}

export const TRIAL_DAYS = 14;

export const PLANS: Record<PlanId, PlanSpec> = {
  free: {
    id: 'free',
    label: 'Free',
    minSeats: 1,
    maxSeats: 1,
    maxViewers: 2,
    storageGb: 5,
    storagePerExtraSeatGb: 0,
    renderMinutes: 30,
    renderMinutesPerExtraSeat: 0,
    aiMinutes: 60,
    aiMinutesPerExtraSeat: 0,
    maxExportShortSide: 720,
    watermark: true,
    exportRetentionDays: 7,
    maxProjects: 3,
    reviewLinkMaxDays: 7,
    reviewPro: false,
    reviewBranding: false,
    mcp: false,
    masterFormats: false,
    gpu: false,
    ownWorkers: false,
    accurateTranscripts: false,
    priority: false,
    invoicing: false,
  },
  // Pro features with small allowances, so a trial cannot be farmed.
  trial: {
    id: 'trial',
    label: 'Pro trial',
    minSeats: 1,
    maxSeats: 5,
    maxViewers: Infinity,
    storageGb: 50,
    storagePerExtraSeatGb: 0,
    renderMinutes: 120,
    renderMinutesPerExtraSeat: 0,
    aiMinutes: 120,
    aiMinutesPerExtraSeat: 0,
    maxExportShortSide: 2160,
    watermark: false,
    exportRetentionDays: 90,
    maxProjects: Infinity,
    reviewLinkMaxDays: 365,
    reviewPro: true,
    reviewBranding: false,
    mcp: true,
    masterFormats: true,
    gpu: false,
    ownWorkers: false,
    accurateTranscripts: false,
    priority: false,
    invoicing: false,
  },
  pro: {
    id: 'pro',
    label: 'Pro',
    minSeats: 1,
    maxSeats: 5,
    maxViewers: Infinity,
    storageGb: 250,
    storagePerExtraSeatGb: 100,
    renderMinutes: 600,
    renderMinutesPerExtraSeat: 600,
    aiMinutes: 600,
    aiMinutesPerExtraSeat: 600,
    maxExportShortSide: 2160,
    watermark: false,
    exportRetentionDays: 90,
    maxProjects: Infinity,
    reviewLinkMaxDays: 365,
    reviewPro: true,
    reviewBranding: false,
    mcp: true,
    masterFormats: true,
    gpu: false,
    ownWorkers: false,
    accurateTranscripts: false,
    priority: false,
    invoicing: false,
  },
  team: {
    id: 'team',
    label: 'Team',
    minSeats: 3,
    maxSeats: 500,
    maxViewers: Infinity,
    storageGb: 1000,
    storagePerExtraSeatGb: 250,
    renderMinutes: 2000,
    renderMinutesPerExtraSeat: 2000,
    aiMinutes: 2000,
    aiMinutesPerExtraSeat: 2000,
    maxExportShortSide: 4320,
    watermark: false,
    exportRetentionDays: 365,
    maxProjects: Infinity,
    reviewLinkMaxDays: 365,
    reviewPro: true,
    reviewBranding: true,
    mcp: true,
    masterFormats: true,
    gpu: true,
    ownWorkers: true,
    accurateTranscripts: true,
    priority: true,
    invoicing: false,
  },
  // Team's features for larger organizations, with invoices and payment terms.
  enterprise: {
    id: 'enterprise',
    label: 'Enterprise',
    minSeats: 10,
    maxSeats: 10_000,
    maxViewers: Infinity,
    storageGb: 10_000,
    storagePerExtraSeatGb: 500,
    renderMinutes: 20_000,
    renderMinutesPerExtraSeat: 3000,
    aiMinutes: 20_000,
    aiMinutesPerExtraSeat: 3000,
    maxExportShortSide: 4320,
    watermark: false,
    exportRetentionDays: 730,
    maxProjects: Infinity,
    reviewLinkMaxDays: 365,
    reviewPro: true,
    reviewBranding: true,
    mcp: true,
    masterFormats: true,
    gpu: true,
    ownWorkers: true,
    accurateTranscripts: true,
    priority: true,
    invoicing: true,
  },
};

/**
 * Deployments without billing (no Stripe: self-hosting, development) put
 * every organization on this plan: everything allowed, nothing metered except
 * the storage quota (DEFAULT_ORG_STORAGE_GB).
 */
export const UNLIMITED_PLAN: PlanSpec = {
  ...PLANS.team,
  label: 'Unlimited',
  minSeats: 1,
  maxSeats: Infinity,
  storageGb: 50,
  storagePerExtraSeatGb: 0,
  renderMinutes: Infinity,
  renderMinutesPerExtraSeat: 0,
  aiMinutes: Infinity,
  aiMinutesPerExtraSeat: 0,
  exportRetentionDays: Infinity,
  priority: false,
};

export interface BillingMode {
  /** Plans and allowances apply (Stripe configured). */
  enabled: boolean;
  /** Storage per organization when billing is off (GB). */
  defaultStorageGb?: number;
}

/** List prices (USD per editor seat per month) shown when Stripe is not configured. */
export const LIST_PRICES: Record<PaidPlanId, Record<BillingInterval, number>> = {
  pro: { month: 18, year: 15 },
  team: { month: 32, year: 27 },
  enterprise: { month: 49, year: 42 },
};

/** Add-ons: extra storage (recurring, per unit) and render-minute packs (one-off, this month). */
export const STORAGE_ADDON_GB = 1000;
export const RENDER_PACK_MINUTES = 500;
export const ADDON_LIST_PRICES = { storagePerMonth: 25, renderPack: 10 };

export interface OrgBillingFields {
  plan: string;
  /** Paid editor seats (Stripe quantity). */
  seats: number;
  /** Extra storage from add-ons (GB). */
  extraStorageGb: number;
  /** Superadmin override of the storage limit (GB); 0 = use the plan. */
  customStorageGb: number;
  trialEndsAt?: { microsSinceUnixEpoch: bigint } | null;
}

const GB = 1024 ** 3;

/** The plan in force: an ended trial is the free plan; unknown plans are free. */
export function effectivePlan(org: Pick<OrgBillingFields, 'plan' | 'trialEndsAt'>, nowMicros: bigint, mode: BillingMode = { enabled: true }): PlanSpec {
  if (!mode.enabled) return { ...UNLIMITED_PLAN, storageGb: mode.defaultStorageGb || UNLIMITED_PLAN.storageGb };
  const id = (PLAN_IDS as readonly string[]).includes(org.plan) ? (org.plan as PlanId) : 'free';
  if (id === 'trial' && org.trialEndsAt && org.trialEndsAt.microsSinceUnixEpoch <= nowMicros) return PLANS.free;
  if (id === 'trial' && !org.trialEndsAt) return PLANS.free;
  return PLANS[id];
}

export interface OrgLimits {
  plan: PlanSpec;
  /** Editor seats paid for (or included). */
  seats: number;
  maxEditors: number;
  maxViewers: number;
  storageBytes: number;
  /** Monthly allowances before one-off packs. */
  renderMinutes: number;
  aiMinutes: number;
}

/** What an organization may use right now. */
export function orgLimits(org: OrgBillingFields, nowMicros: bigint, mode: BillingMode = { enabled: true }): OrgLimits {
  const plan = effectivePlan(org, nowMicros, mode);
  const paid = mode.enabled && isPaidPlan(plan.id);
  const seats = paid ? Math.min(plan.maxSeats, Math.max(plan.minSeats, Math.floor(org.seats) || plan.minSeats)) : plan.minSeats;
  const extra = seats - 1;
  const storageGb = org.customStorageGb > 0 ? org.customStorageGb : plan.storageGb + plan.storagePerExtraSeatGb * extra + (paid ? Math.max(0, org.extraStorageGb) : 0);
  return {
    plan,
    seats,
    maxEditors: paid ? seats : plan.maxSeats,
    maxViewers: plan.maxViewers,
    storageBytes: storageGb * GB,
    renderMinutes: plan.renderMinutes + plan.renderMinutesPerExtraSeat * extra,
    aiMinutes: plan.aiMinutes + plan.aiMinutesPerExtraSeat * extra,
  };
}

/** Usage month of a timestamp: "YYYY-MM" (UTC). */
export function usagePeriod(micros: bigint): string {
  return new Date(Number(micros / 1000n)).toISOString().slice(0, 7);
}

/** Whether a role takes an editor seat. */
export const isEditorRole = (role: string) => role === 'owner' || role === 'admin' || role === 'editor';

type WeighedSettings = { width: number; height: number; format?: string; quality?: string; encode?: Partial<EncodeSettings> };

/** Resolution weight of an export: 720p ½, 1080p 1, 1440p 2, 4K and up 4; master formats / lossless double. */
export function renderWeight(s: WeighedSettings): number {
  const short = Math.min(s.width, s.height);
  const base = short <= 720 ? 0.5 : short <= 1080 ? 1 : short <= 1440 ? 2 : 4;
  const master = s.encode ? isMasterEncode(normalizeEncode(s.encode)) : s.format === 'mov_prores' || s.quality === 'lossless';
  return master ? base * 2 : base;
}

/** Render minutes an export of `durationUs` uses (rounded up to a hundredth). */
export function renderMinutesFor(s: WeighedSettings, durationUs: number): number {
  return Math.ceil((Math.max(0, durationUs) / 60e6) * renderWeight(s) * 100) / 100;
}

/** AI minutes for media of `durationUs` (rounded up to a hundredth). */
export function aiMinutesFor(durationUs: number): number {
  return Math.ceil((Math.max(0, durationUs) / 60e6) * 100) / 100;
}

/** Spoken length of a voiceover script (about 15 characters a second at speed 1). */
export function voiceoverDurationUs(text: string, speed = 1): number {
  return (text.length / 15 / Math.max(0.5, speed)) * 1e6;
}

/** Rough size of an export, for checking it fits in the storage left (bytes). */
export function estimateExportBytes(s: { width: number; height: number; fps: number; format?: string; quality?: string; encode?: Partial<EncodeSettings> }, durationUs: number): number {
  if (s.encode) return estimateEncodeBytes(normalizeEncode(s.encode), { ...s, durationUs });
  const pixelsPerSec = s.width * s.height * Math.max(1, s.fps);
  const bitsPerPixel = s.format === 'mov_prores' ? 1.8 : s.quality === 'lossless' ? 2 : s.quality === 'high' ? 0.12 : s.quality === 'low' ? 0.04 : 0.07;
  const videoBps = pixelsPerSec * bitsPerPixel;
  return ((videoBps + 256_000) / 8) * (Math.max(0, durationUs) / 1e6);
}

export const WATERMARK_TEXT = 'Made with vidmakr';

/**
 * Adds the free plan's mark to a render: a small, half-transparent line of
 * text in the bottom-right corner on a new top track, from `startUs` to `endUs`.
 */
export function addWatermark(snap: TimelineSnapshot, startUs: number, endUs: number): TimelineSnapshot {
  const { width: w, height: h } = snap.project;
  if (!(endUs > startUs)) return snap;
  const fontSize = Math.max(14, Math.round(Math.min(w, h) * 0.026));
  // Text is centred on (w/2 + x, h/2 + y); its width is estimated from the glyph count.
  const textW = fontSize * 0.56 * WATERMARK_TEXT.length;
  const margin = Math.round(Math.min(w, h) * 0.03);
  const trackId = 'vm-watermark';
  const order = Math.max(0, ...snap.tracks.map((t) => t.order)) + 1;
  const track: TrackData = { id: trackId, projectId: snap.project.id, kind: 'video', name: 'Watermark', order, muted: true, hidden: false, locked: true, solo: false, volume: 1, pan: 0 };
  const clip: ClipData = {
    id: 'vm-watermark',
    projectId: snap.project.id,
    trackId,
    kind: 'text',
    assetId: null,
    name: 'Watermark',
    startUs,
    durationUs: endUs - startUs,
    inUs: 0,
    speed: 1,
    transform: { ...DEFAULT_TRANSFORM, x: Math.round(w / 2 - margin - textW / 2), y: Math.round(h / 2 - margin - fontSize * 0.7), opacity: 0.6 },
    crop: DEFAULT_CROP,
    color: DEFAULT_COLOR,
    videoFx: DEFAULT_VIDEO_FX,
    videoFadeInUs: 0,
    videoFadeOutUs: 0,
    transitionIn: null,
    transitionOut: null,
    volume: 0,
    muted: true,
    audioFadeInUs: 0,
    audioFadeOutUs: 0,
    audioFx: DEFAULT_AUDIO_FX,
    text: { ...DEFAULT_TEXT, content: WATERMARK_TEXT, fontSize, fontWeight: 600, shadowBlur: 4, shadowY: 1 },
    fillColor: '#000000',
    keyframes: [],
    linkId: null,
    ext: '',
  };
  return { ...snap, tracks: [...snap.tracks, track], clips: [...snap.clips, clip] };
}
